import React from 'react';
import Image from "next/image";
import Text from '@components/ui-old/text';
import cn from 'classnames';
import Link from '@components/ui-old/link';
import { useTranslation } from 'next-i18next';
import { useSsrCompatible } from '@utils/use-ssr-compatible';
import { getCurrentLocationAndLanguage } from '@utils/location';
import { IconDownload } from '@tabler/icons-react';
import IOSInstallPrompt from '@components/other/ios-install-pwa';
const data = {
  title: 'app-heading',
  subTitle: 'app-sub-heading',
  appImage: '/assets/images/app/app-pwa-s24-en.png',
  appImage2: '/assets/images/app/app-pwa-iphone-en.png',
  appImage3: '/assets/images/app/app-pwa-s24-ru.png',
  appImage4: '/assets/images/app/app-pwa-iphone-ru.png',
  appButtons: [{
    id: 1,
    slug: '/pwa-download',
    altText: 'button-download-app',
    appButton: '',
    buttonWidth: 209,
    buttonHeight: 60
  }]
};
interface Props {
  className?: string;
  variant?: 'modern' | 'ancient';
  disableBorderRadius?: boolean;
}
const DownloadApps: React.FC<Props> = ({
  className,
  disableBorderRadius = false
}) => {
  const {
    appButtons,
    title,
    subTitle,
    appImage,
    appImage2,
    appImage3,
    appImage4
  } = data;
  const {
    t
  } = useTranslation('common');
  const hasMounted = useSsrCompatible(true, false);
  const {
    location
  } = getCurrentLocationAndLanguage();
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
  const [iosPromptForced, setIosPromptForced] = React.useState(false);
  const [isPwaInstalled, setIsPwaInstalled] = React.useState(false);
  const isRuLocation = ['kg', 'kz', 'uz'].includes(location);
  const primaryImage = isRuLocation ? appImage3 : appImage;
  const secondaryImage = isRuLocation ? appImage4 : appImage2;
  React.useEffect(() => {
    const storedFlag = localStorage.getItem('pwaInstalled') === 'true';
    if (storedFlag) {
      setIsPwaInstalled(true);
    }
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-ignore
    window.navigator.standalone === true;
    if (isStandalone) {
      localStorage.setItem('pwaInstalled', 'true');
      setIsPwaInstalled(true);
    }
    const onAppInstalled = () => {
      localStorage.setItem('pwaInstalled', 'true');
      setIsPwaInstalled(true);
    };
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);
  const handleInstallClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const isIOS = () => typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS()) {
      setIosPromptForced(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {} else {}
        setDeferredPrompt(null);
      });
    }
  };
  if (isPwaInstalled) {
    return null;
  }
  return <div className={cn('flex justify-between items-end rounded-lg pt-5 md:pt-4 lg:pt-5 xl:pt-6 px-6 md:px-12 lg:px-20 2xl:px-24 3xl:px-36', className)} data-sentry-component="DownloadApps" data-sentry-source-file="download-apps.tsx">
      <div className='flex-shrink-0 w-full pb-5 sm:w-60 md:w-96 lg:w-auto lg:max-w-lg xl:max-w-xl lg:flex lg:items-center md:pb-8 lg:pb-12 xl:pb-16'>
        <div className='py-4 text-center md:py-6 xl:py-8 ltr:sm:text-left rtl:sm:text-right'>
          <Text variant='mediumHeading' className='-mt-1 mb-2 md:mb-3 lg:mb-3.5 xl:mb-4' data-sentry-element="Text" data-sentry-source-file="download-apps.tsx">
            {t(`${title}`)}
          </Text>
          {hasMounted && <h2 className='mb-6 font-normal leading-7 text-heading text-md sm:text-xl md:text-3xl xl:text-4xl 2xl:text-5xl sm:leading-8 md:leading-snug xl:leading-relaxed 2xl:leading-snug md:mb-8 lg:mb-9 xl:mb-12 2xl:mb-14 ltr:lg:pr-20 ltr:2xl:pr-0 rtl:lg:pl-20 rtl:2xl:pl-0' dangerouslySetInnerHTML={{
          __html: t(`${subTitle}`)
        }} />}
          <div className='flex justify-center px-6 sm:justify-start gap-x-2 md:gap-x-3 sm:px-0'>
            {appButtons?.map(item => item.slug === '/pwa-download' ? <button key={item.id} onClick={handleInstallClick} className='inline-flex transition duration-200 ease-in hover:box-shadow hover:opacity-80'>
                  <span className={`w-36 lg:w-44 xl:w-auto border border-neutral-800 p-2 bg-neutral-900 text-neutral-100 text-lg lg:text-xl flex items-center gap-2 w-fit ${!disableBorderRadius ? 'rounded-xl' : ''}`}>{t(`${item.altText}`)} <IconDownload stroke={2} size={24} className='text-neutral-100' /></span>
                </button> : <Link key={item.id} href={item.slug} className='inline-flex transition duration-200 ease-in hover:box-shadow hover:opacity-80'>
                  <span className={`w-36 lg:w-44 xl:w-auto ${!disableBorderRadius ? 'rounded-xl' : ''}`}>{t(`${item.altText}`)}</span>
                </Link>)}
          </div>
        </div>
      </div>
      <div className='hidden sm:flex items-end ltr:pl-4 rtl:pr-4 ltr:-mr-0.5 rtl:-ml-0.5 ltr:2xl:-mr-1.5 rtl:2xl:-ml-1.5 w-60 md:w-72 lg:w-96 xl:w-auto'>
        <Image src={primaryImage} alt={t('text-app-thumbnail') + " - Primary"} width={375} height={430} style={{
        maxWidth: "100%",
        height: "500px"
      }} data-sentry-element="Image" data-sentry-source-file="download-apps.tsx" />
        <Image src={secondaryImage} alt={t('text-app-thumbnail') + " - Secondary"} width={375} height={430} style={{
        maxWidth: "100%",
        height: "500px",
        marginLeft: "-4rem"
      }} data-sentry-element="Image" data-sentry-source-file="download-apps.tsx" />
      </div>
      <IOSInstallPrompt forceVisible={iosPromptForced} onDismiss={() => setIosPromptForced(false)} data-sentry-element="IOSInstallPrompt" data-sentry-source-file="download-apps.tsx" />
    </div>;
};
export default DownloadApps;